import { fetchReady } from "@/api/client";
import { type Component, For, createResource } from "solid-js";
import { statusBadge } from "./landingStatus";

// Public Google Form intake for the invite-only beta. Visitors request
// access here; approved requests become invite codes via the admin invite
// system, which the invitee then redeems after signing in.
const ACCESS_REQUEST_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSdQWpFHSrsrzE4G8uLSd_X1K7k2PP9TmIEQ-MjEb7MB520vUg/viewform";

const SURFACES = [
  {
    category: "Chat",
    lead: "gpt-oss:120b · pinned",
    body: "Search, browse the live web, images, files.",
    meta: [
      ["Models", "gpt-oss:120b now"],
      ["Tools", "search · browse · image · files"],
    ],
  },
  {
    category: "VM",
    lead: "NVIDIA · isolated · ephemeral",
    body: "Isolated CPU or GPU. Browser terminal. Ephemeral by default.",
    meta: [
      ["GPUs", "NVIDIA"],
      ["Access", "invite-only"],
      ["Terminal", "browser attach"],
    ],
  },
  {
    category: "CLI",
    lead: "v0.1.0 · cargo install",
    body: "Cockpit on your machine. Spawn Claude, Codex, and others in parallel tmux. Watch live, attach when you need to.",
    meta: [
      ["Latest", "v0.1.0"],
      ["Install", "cargo install"],
      ["Platforms", "macOS · Linux"],
    ],
  },
] as const;

interface Props {
  onLogin: () => void;
}

const LandingPage: Component<Props> = (props) => {
  // Live platform status from the public /readyz probe (no auth). fetchReady
  // never throws and degrades a network failure to "down".
  const [ready] = createResource(fetchReady);
  const badge = () => statusBadge(ready()?.status);

  return (
    <main class="landing-page">
      <header class="landing-top">
        <div class="landing-status">
          <span class={`landing-dot tone-${badge().tone}`} />
          {badge().label}
        </div>
        <div class="landing-brand">z01.ai</div>
        <nav aria-label="public links">
          <button type="button" onClick={props.onLogin}>
            Sign in
          </button>
        </nav>
      </header>

      <section class="landing-hero">
        <p class="landing-kicker">Research cluster</p>
        <h1>Ask. Measure. Repeat.</h1>
        <p class="landing-subhead">
          Open-weight inference, an ephemeral GPU sandbox, and a CLI that runs
          the loop from your machine. Same weights next week — every run is
          reproducible.
        </p>
        <div class="landing-cta-row">
          <a
            class="landing-cta"
            href={ACCESS_REQUEST_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Request access
          </a>
          <p class="landing-cta-note">Invite-only beta</p>
        </div>
      </section>

      <section class="landing-grid" aria-label="z01 surfaces">
        <For each={SURFACES}>
          {(surface) => (
            <article class="landing-col">
              <div class="landing-category">{surface.category}</div>
              <h2>{surface.lead}</h2>
              <p>{surface.body}</p>
              <div class="landing-meta">
                <For each={surface.meta}>
                  {([label, value]) => (
                    <div>
                      <span>{label}</span>
                      <b>{value}</b>
                    </div>
                  )}
                </For>
              </div>
            </article>
          )}
        </For>
      </section>
    </main>
  );
};

export default LandingPage;
